import { createContext, useContext, ReactNode, useState, useEffect, useCallback } from "react";

// Supervisor type matching shift_supervisors structure
export type SupervisorType = {
  user_id: string;
  user?: {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    preferred_name?: string | null;
    email_address?: string | null;
    meta_data?: Record<string, unknown> | null;
  };
  permissions?: Record<string, unknown>;
  meta_data?: Record<string, unknown>;
};

type LocationType = {
  id: string;
  name: string;
  address?: { street?: string; city?: string; state?: string; zip?: string };
  market_id?: string;
};

interface SupervisorsContextValue {
  supervisors: SupervisorType[];
  loading: boolean;
  shiftId?: string;
  // Market data for filtering
  shiftMarkets?: string[];
  projectMarkets?: string[];
  shiftLocation?: LocationType;
  projectLocation?: LocationType;
  // CRUD operations
  addSupervisor: (supervisor: SupervisorType) => void;
  removeSupervisor: (userId: string) => void;
}

const SupervisorsContext = createContext<SupervisorsContextValue | null>(null);

export function SupervisorsProvider({
  children,
  supervisors: initialSupervisors,
  loading = false,
  shiftId,
  shiftLocation,
  projectLocation,
  shiftMarkets,
  projectMarkets,
}: {
  children: ReactNode;
  supervisors: SupervisorType[];
  loading?: boolean;
  shiftId?: string;
  shiftLocation?: LocationType;
  projectLocation?: LocationType;
  shiftMarkets?: string[];
  projectMarkets?: string[];
}) {
  const [supervisors, setSupervisors] = useState<SupervisorType[]>(initialSupervisors);

  // Sync state when prop changes (e.g., shift data refreshed)
  useEffect(() => {
    setSupervisors(initialSupervisors);
  }, [initialSupervisors]);

  const addSupervisor = useCallback((supervisor: SupervisorType) => {
    setSupervisors((prev) => {
      // Prevent duplicates
      if (prev.some((s) => s.user_id === supervisor.user_id)) return prev;
      return [...prev, supervisor];
    });
  }, []);

  const removeSupervisor = useCallback((userId: string) => {
    setSupervisors((prev) => prev.filter((s) => s.user_id !== userId));
  }, []);

  return (
    <SupervisorsContext.Provider
      value={{
        supervisors,
        loading,
        shiftId,
        shiftLocation,
        projectLocation,
        shiftMarkets,
        projectMarkets,
        addSupervisor,
        removeSupervisor,
      }}
    >
      {children}
    </SupervisorsContext.Provider>
  );
}

export function useSupervisors() {
  const context = useContext(SupervisorsContext);
  if (!context) {
    throw new Error("useSupervisors must be used within a SupervisorsProvider");
  }
  return context;
}
